/**
 * FetchLatestUsersUseCase
 *
 * Fetches one page of the admin Latest Users list, newest-first, for the
 * chosen sort field (created vs last-seen timestamp). Mirrors Flutter's
 * `latest_users_screen.dart` flow: the screen calls
 * `UsersRepository.getUsers(lastFetchedItem, AppConstants.usersListPagination,
 * _orderBy.field)` and `addAllUnique`s the returned users
 * (`lib/presentation/screens/user/latest_users_screen.dart:85-92`).
 *
 * Single responsibility: orchestrate the paginated fetch and forward the
 * service's pagination state unchanged. No blocked-user filtering is applied
 * (Flutter applies none on this admin list — see `UserService.fetchLatestUsers`).
 */

import type { PageCursor } from '@/data/types/json';
import { UserService } from '@/domain/services/user/userService';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { LastUsersOrderBy } from '@/domain/enums/user/lastUsersOrderBy';

export interface FetchLatestUsersParams {
  orderBy: LastUsersOrderBy;
  cursor?: PageCursor;
}

export interface FetchLatestUsersResult {
  users: UserEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class FetchLatestUsersUseCase {
  private userService: UserService;

  constructor() {
    this.userService = UserService.getInstance();
  }

  async execute(params: FetchLatestUsersParams): Promise<FetchLatestUsersResult> {
    const { orderBy, cursor } = params;

    const result = await this.userService.fetchLatestUsers(orderBy, cursor);

    return {
      users: result.users,
      hasMore: result.hasMore,
      cursor: result.cursor,
    };
  }
}

export const createFetchLatestUsersUseCase = () => new FetchLatestUsersUseCase();
